import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { useOnboardingChecklist } from "@/hooks/use-api";
import { useImpersonation, ImpersonationProvider } from "@/providers/impersonation-provider";
import { AppShell } from "@/components/layout/app-shell";
import { ProductionAppShell } from "@/components/layout/production-app-shell";
import { ErrorBoundary } from "@/components/error-boundary";
import LandingPage from "@/pages/landing";
import LoginPage from "@/pages/login";
import SignupPage from "@/pages/signup";
import CallbackPage from "@/pages/callback";
import NoTenantPage from "@/pages/no-tenant";
import VerifyEmailPage from "@/pages/verify-email";
import SetupPage from "@/pages/setup";
import DashboardPage from "@/pages/dashboard";
import AlertsPage from "@/pages/alerts";
import IntegrationsPage from "@/pages/integrations";
import HostsPage from "@/pages/hosts";
import InvestigationPage from "@/pages/investigation";
import MSPPortalPage from "@/pages/msp";
import SettingsPage from "@/pages/settings";
import UsagePage from "@/pages/usage";
import CompliancePage from "@/pages/compliance";
import EmailAuthPage from "@/pages/email-auth";
import ThreatIntelPage from "@/pages/threat-intel";
import VulnerabilitiesPage from "@/pages/vulnerabilities";
import CloudPosturePage from "@/pages/cloud-posture";
import ExecutivePage from "@/pages/executive";
import SuperadminDashboardPage from "@/pages/superadmin/index";
import SuperadminTenantsPage from "@/pages/superadmin/tenants";
import SuperadminUsersPage from "@/pages/superadmin/users";
import { lazy, Suspense } from "react";
import { DocsLayout } from "@/components/layout/docs-layout";
import {
  APP_BASE,
  APP_ROUTES,
  CLASSIC_APP_BASE,
  PREVIEW_APP_BASE,
  canonicalAppLocation,
} from "@/lib/app-routes";

const DocsOverview = lazy(() => import("@/pages/docs/index"));
const DocsApiReference = lazy(() => import("@/pages/docs/api"));
const IntegrationsGuide = lazy(() => import("@/pages/docs/guides/integrations"));
const AgentSetupGuide = lazy(() => import("@/pages/docs/guides/agent-setup"));
const ApiKeysGuide = lazy(() => import("@/pages/docs/guides/api-keys"));
const AlertsResponseGuide = lazy(() => import("@/pages/docs/guides/alerts-response"));
const SearchInvestigationGuide = lazy(() => import("@/pages/docs/guides/search-investigation"));
const AccountSecurityGuide = lazy(() => import("@/pages/docs/guides/account-security"));
const DataPrivacyGuide = lazy(() => import("@/pages/docs/guides/data-privacy"));
const MspGuide = lazy(() => import("@/pages/docs/guides/msp"));
const LogsPage = lazy(() => import("@/pages/logs"));
const OAuthMicrosoftCallback = lazy(() => import("./pages/oauth-microsoft-callback"));
const RefundCancellationPolicyPage = lazy(
  () => import("@/pages/refund-cancellation-policy"),
);
const TermsOfServicePage = lazy(() => import("@/pages/terms-of-service"));
const PrivacyPolicyPage = lazy(() => import("@/pages/privacy-policy"));
const DataProcessingAddendumPage = lazy(
  () => import("@/pages/data-processing-addendum"),
);

const policyFallback = (
  <div className="flex h-screen items-center justify-center bg-background">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
  </div>
);

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function SuperadminRoute({ children, fallback }: { children: React.ReactNode; fallback: string }) {
  const { user } = useAuth();
  if (!user?.isSuperadmin) {
    return <Navigate to={fallback} replace />;
  }
  return <>{children}</>;
}

/** Shows LandingPage for unauthenticated users, redirects to /app for authenticated. */
function RootRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={APP_BASE} replace />;
  }

  return <LandingPage />;
}

/**
 * Guards authenticated routes — redirects new tenants to /setup if they
 * haven't completed any onboarding steps and haven't explicitly skipped.
 * Superadmin impersonation always passes through.
 */
function FirstRunGuard({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { targetTenantId } = useImpersonation();
  const { data: checklist, isLoading } = useOnboardingChecklist(user?.tenantId);

  // Superadmin impersonating a tenant — don't gate them
  if (targetTenantId) {
    return <>{children}</>;
  }

  // Still loading checklist — show skeleton
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  // Check if user has skipped onboarding
  const skipped = localStorage.getItem(`onboarding_skipped_${user?.tenantId}`) === "true";
  if (skipped) {
    return <>{children}</>;
  }

  // If checklist loaded and 0 completed steps, redirect to setup
  if (checklist) {
    const completedCount = checklist.steps?.filter((s) => s.status === "completed").length ?? 0;
    if (completedCount === 0) {
      return <Navigate to="/setup" replace />;
    }
  }

  return <>{children}</>;
}

function AuthenticatedAppRoutes({ fallback }: { fallback: string }) {
  return (
    <Routes>
      <Route path="/" element={<ErrorBoundary><DashboardPage /></ErrorBoundary>} />
      <Route path="/alerts" element={<ErrorBoundary><AlertsPage /></ErrorBoundary>} />
      <Route path="/alerts/:alertId" element={<ErrorBoundary><AlertsPage /></ErrorBoundary>} />
      <Route path="/integrations" element={<ErrorBoundary><IntegrationsPage /></ErrorBoundary>} />
      <Route path="/hosts" element={<ErrorBoundary><HostsPage /></ErrorBoundary>} />
      <Route path="/investigation" element={<ErrorBoundary><InvestigationPage /></ErrorBoundary>} />
      <Route path="/msp" element={<ErrorBoundary><MSPPortalPage /></ErrorBoundary>} />
      <Route path="/usage" element={<ErrorBoundary><UsagePage /></ErrorBoundary>} />
      <Route path="/settings" element={<ErrorBoundary><SettingsPage /></ErrorBoundary>} />
      <Route path="/settings/:settingsSection" element={<ErrorBoundary><SettingsPage /></ErrorBoundary>} />
      <Route path="/compliance" element={<ErrorBoundary><CompliancePage /></ErrorBoundary>} />
      <Route path="/email-auth" element={<ErrorBoundary><EmailAuthPage /></ErrorBoundary>} />
      <Route path="/threat-intel" element={<ErrorBoundary><ThreatIntelPage /></ErrorBoundary>} />
      <Route path="/vulnerabilities" element={<ErrorBoundary><VulnerabilitiesPage /></ErrorBoundary>} />
      <Route path="/cloud-posture" element={<ErrorBoundary><CloudPosturePage /></ErrorBoundary>} />
      <Route
        path="/logs"
        element={
          <ErrorBoundary>
            <Suspense fallback={<PageSpinner />}>
              <LogsPage />
            </Suspense>
          </ErrorBoundary>
        }
      />
      <Route path="/executive" element={<ErrorBoundary><ExecutivePage /></ErrorBoundary>} />
      <Route path="/superadmin" element={<SuperadminRoute fallback={fallback}><ErrorBoundary><SuperadminDashboardPage /></ErrorBoundary></SuperadminRoute>} />
      <Route path="/superadmin/tenants" element={<SuperadminRoute fallback={fallback}><ErrorBoundary><SuperadminTenantsPage /></ErrorBoundary></SuperadminRoute>} />
      <Route path="/superadmin/users" element={<SuperadminRoute fallback={fallback}><ErrorBoundary><SuperadminUsersPage /></ErrorBoundary></SuperadminRoute>} />
      <Route path="*" element={<Navigate to={fallback} replace />} />
    </Routes>
  );
}

function PageSpinner() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  );
}

function PreviewRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={canonicalAppLocation(location.pathname, location.search, location.hash)}
      replace
    />
  );
}

export default function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<RootRoute />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/callback" element={<CallbackPage />} />
      <Route path="/oauth/microsoft/callback" element={<OAuthMicrosoftCallback />} />
      <Route
        path="/refund-cancellation-policy"
        element={
          <Suspense
            fallback={
              <div className="flex h-screen items-center justify-center bg-background">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            }
          >
            <RefundCancellationPolicyPage />
          </Suspense>
        }
      />
      <Route
        path="/terms-of-service"
        element={<Suspense fallback={policyFallback}><TermsOfServicePage /></Suspense>}
      />
      <Route
        path="/privacy-policy"
        element={<Suspense fallback={policyFallback}><PrivacyPolicyPage /></Suspense>}
      />
      <Route
        path="/data-processing-addendum"
        element={<Suspense fallback={policyFallback}><DataProcessingAddendumPage /></Suspense>}
      />
      {/* Reached when the API returns 403 no_tenant or when api.ts detects a
          401 redirect loop. Static page, no authenticated API calls. */}
      <Route path="/no-tenant" element={<NoTenantPage />} />

      {/* Documentation — public, own layout */}
      <Route
        path="/docs/*"
        element={
          <DocsLayout>
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-20">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                </div>
              }
            >
              <Routes>
                <Route path="/" element={<DocsOverview />} />
                <Route path="/api" element={<DocsApiReference />} />
                <Route path="/guides/integrations" element={<IntegrationsGuide />} />
                <Route path="/guides/agent-setup" element={<AgentSetupGuide />} />
                <Route path="/guides/api-keys" element={<ApiKeysGuide />} />
                <Route path="/guides/alerts-response" element={<AlertsResponseGuide />} />
                <Route path="/guides/search-investigation" element={<SearchInvestigationGuide />} />
                <Route path="/guides/account-security" element={<AccountSecurityGuide />} />
                <Route path="/guides/data-privacy" element={<DataPrivacyGuide />} />
                <Route path="/guides/msp" element={<MspGuide />} />
                <Route path="*" element={<Navigate to="/docs" replace />} />
              </Routes>
            </Suspense>
          </DocsLayout>
        }
      />

      {/* Setup — authenticated but outside AppShell */}
      <Route
        path="/setup"
        element={
          <ProtectedRoute>
            <ImpersonationProvider>
              <ErrorBoundary>
                <SetupPage />
              </ErrorBoundary>
            </ImpersonationProvider>
          </ProtectedRoute>
        }
      />

      {/* The parallel preview is now canonical. Preserve deep links and queries. */}
      <Route path={`${PREVIEW_APP_BASE}/*`} element={<PreviewRedirect />} />

      {/* Canonical authenticated application using the production design shell. */}
      <Route
        path={`${APP_BASE}/*`}
        element={
          <ProtectedRoute>
            <ImpersonationProvider>
              <FirstRunGuard>
                <ProductionAppShell>
                  <AuthenticatedAppRoutes fallback={APP_BASE} />
                </ProductionAppShell>
              </FirstRunGuard>
            </ImpersonationProvider>
          </ProtectedRoute>
        }
      />

      {/* Authenticated fallback retained for the first production release. */}
      <Route
        path={`${CLASSIC_APP_BASE}/*`}
        element={
          <ProtectedRoute>
            <ImpersonationProvider>
              <FirstRunGuard>
                <AppShell basePath={CLASSIC_APP_BASE}>
                  <AuthenticatedAppRoutes fallback={CLASSIC_APP_BASE} />
                </AppShell>
              </FirstRunGuard>
            </ImpersonationProvider>
          </ProtectedRoute>
        }
      />

      {/* Redirects for old paths */}
      <Route path="/alerts" element={<Navigate to={APP_ROUTES.alerts} replace />} />
      <Route path="/integrations" element={<Navigate to={APP_ROUTES.integrations} replace />} />
      <Route path="/hosts" element={<Navigate to={APP_ROUTES.hosts} replace />} />
      <Route path="/investigation" element={<Navigate to={APP_ROUTES.investigation} replace />} />
      <Route path="/msp" element={<Navigate to={APP_ROUTES.msp} replace />} />
      <Route path="/usage" element={<Navigate to={APP_ROUTES.usage} replace />} />
      <Route path="/settings" element={<Navigate to={APP_ROUTES.settings} replace />} />
      <Route path="/compliance" element={<Navigate to={APP_ROUTES.compliance} replace />} />
      <Route path="/logs" element={<Navigate to={APP_ROUTES.logs} replace />} />
      <Route path="/threat-intel" element={<Navigate to={APP_ROUTES.threatIntel} replace />} />
      <Route path="/vulnerabilities" element={<Navigate to={APP_ROUTES.vulnerabilities} replace />} />
      <Route path="/cloud-posture" element={<Navigate to={APP_ROUTES.cloudPosture} replace />} />
      <Route path="/executive" element={<Navigate to={APP_ROUTES.executive} replace />} />
      <Route path="/superadmin" element={<Navigate to={APP_ROUTES.superadmin} replace />} />
      <Route path="/superadmin/tenants" element={<Navigate to={APP_ROUTES.superadminTenants} replace />} />
      <Route path="/superadmin/users" element={<Navigate to={APP_ROUTES.superadminUsers} replace />} />

      {/* Catch-all: redirect unknown top-level routes */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
